/**
 * The organization an email link is about (#1232).
 *
 * Every link an email sends into the app carries `?org=<orgId>`
 * (`appLinkForOrg` in @aica/core). Without it, a member of organizations A and
 * B who last looked at B opened A's digest and saw B's data — or an upsell for
 * a tier only B lacks — under A's email.
 *
 * Captured at boot, BEFORE the router is created, for two reasons:
 * - the parameter leaves the address bar without a history entry, and the
 *   router never sees it, so no later navigation can carry it along;
 * - a signed-out reader goes through sign-in first, and Clerk's redirect drops
 *   the original URL. sessionStorage survives that redirect in the same tab and
 *   dies with the tab.
 *
 * The value only SELECTS: `OrganizationContext` switches to it only when the
 * signed-in user's own roster (`/users/me`) contains it, and the server scopes
 * every request to the caller's memberships regardless.
 *
 * Embedded Bitrix24 sessions ignore it: they are bound to their portal's
 * organization and have no organization selection at all.
 *
 * Dependency-free apart from the parameter name, like `lib/referral.ts`: it
 * runs on the synchronous boot path.
 */
import { ORG_LINK_PARAM } from '@aica/core';
import { IS_EMBEDDED_SESSION } from '@/lib/embedded-session';

const STORAGE_KEY = 'aelo_link_org_id';
/** An org id is a UUID; anything far longer is not one and is not kept. */
const MAX_LENGTH = 64;

/**
 * Move `?org=` from the address bar into sessionStorage. No-op when the
 * parameter is absent or the session is embedded. A malformed value is still
 * removed from the URL and still kept: the roster check rejects it and the
 * reader is told the link's organization is not theirs. A value longer than
 * {@link MAX_LENGTH} is removed from the URL and dropped, not truncated: a
 * truncated prefix is a different id, not the one the link named. An empty or
 * dropped value also clears one an earlier link left in this tab.
 */
export function captureOrgFromLink(isEmbedded: boolean = IS_EMBEDDED_SESSION): void {
  if (isEmbedded) return;
  try {
    const search = new URLSearchParams(window.location.search);
    if (!search.has(ORG_LINK_PARAM)) return;
    const orgId = search.get(ORG_LINK_PARAM) ?? '';
    search.delete(ORG_LINK_PARAM);
    const rest = search.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`
    );
    // The latest link decides: a link whose value cannot be kept also clears
    // what an earlier link in this tab left, or sign-in would select an
    // organization the link the reader just opened never named.
    if (orgId && orgId.length <= MAX_LENGTH) {
      window.sessionStorage.setItem(STORAGE_KEY, orgId);
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Never break boot because the link could not be read.
  }
}

/** Return the captured organization once, then forget it. */
export function takeOrgFromLink(): string | null {
  try {
    const orgId = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
    return orgId || null;
  } catch {
    return null;
  }
}
